import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus, showNotification} from 'platform/components';

import {mergeAll} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetVehicleQuery, useUpdateVehicleMutation} from '@dms/api/vehicle';
import {
  BaseVehicle,
  BaseVehicleValidationSchema,
  SERVICE_INTERVALS_DEFAULT_VALUES,
  toAPIVehicle,
  toBaseVehicle,
  useFormNavigate,
  useGetCarFeatures,
  useVehicleFormContext,
  useVehicleVariantSync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {formatDocumentTitle, FullScreenModal} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {ServiceIntervals} from '../(sections)/ServiceIntervals';

interface EditServiceIntervalsFormProps {
  vehicleId: string;
  onDiscard: () => void;
  onSave: (values: BaseVehicle) => void;
  isUpdating: boolean;
}

function EditServiceIntervalsForm({
  vehicleId,
  onDiscard,
  onSave,
  isUpdating,
}: EditServiceIntervalsFormProps) {
  const {formApi} = useVehicleFormContext();
  // Sync vehicle variant (sale / rental / service) data with XState machine
  useVehicleVariantSync(vehicleId);

  return (
    <FullScreenModal
      headline={i18n.t('entity.vehicle.labels.updateServiceIntervals')}
      backgroundColor="general.white"
      scrollable
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isUpdating,
          onClick: onDiscard,
        },
        {
          title: i18n.t('general.actions.save'),
          isLoading: isUpdating,
          onClick: () => {
            formApi.handleSubmit(onSave, (error) => {
              if (i18n.exists('errors.invalid_submit')) {
                showNotification.warning(i18n.t('errors.invalid_submit'));
              }

              if (process.env.ENV_TYPE === 'dev') {
                console.error(error);
              }
            })();
          },
        },
      ]}
    >
      <ServiceIntervals />
    </FullScreenModal>
  );
}

export function EditServiceIntervals() {
  const {navigateOnDiscard, navigateOnConfirm} = useFormNavigate();
  const [updateVehicleMutation, {isLoading: isUpdating}] = useUpdateVehicleMutation();

  const {id: vehicleId} = useRequiredParams();

  // const {
  //   generateSchema,
  //   isMandatoryFieldsEnabled,
  //   isLoading: isMandatoryFieldsLoading,
  // } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );
  // const [formSchema] = useState<ZodSchema<BaseVehicle>>(() =>
  //   generateSchema(BaseVehicleValidationSchema, vehicle?.type as VehicleTypeEnum | undefined)
  // );

  const [getCarFeatures] = useGetCarFeatures();

  const handleSave = async (values: BaseVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features || null;
    const apiPayload = toAPIVehicle(values, existingFeatures, sourcingData);

    updateVehicleMutation({
      vehicleId,
      updateVehicleRequestBody: apiPayload,
      validateMandatoryFields: false,
    })
      .unwrap()
      .then(() => navigateOnConfirm(vehicleId));
  };

  const isLoading = /* isMandatoryFieldsLoading || */ isVehicleLoading;
  const isError = isVehicleError;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.updateServiceIntervals'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VFKit.Form
          experimentalZodSchema={BaseVehicleValidationSchema}
          defaultValues={{
            ...toBaseVehicle(vehicle!),
            serviceIntervals: vehicle?.serviceIntervals
              ? mergeAll([SERVICE_INTERVALS_DEFAULT_VALUES, vehicle.serviceIntervals])
              : SERVICE_INTERVALS_DEFAULT_VALUES,
          }}
        >
          <EditServiceIntervalsForm
            vehicleId={vehicleId}
            onDiscard={navigateOnDiscard}
            onSave={handleSave}
            isUpdating={isUpdating}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
